import { useTranslation } from 'react-i18next';
import TopBar from '../home/components/TopBar';
import MainNav from '../home/components/MainNav';
import Footer from '../home/components/Footer';
import QuickMenu from '../../components/feature/QuickMenu';
import { useSEO, createServiceSchema } from '../../utils/seo';

export default function PublicSolutionPage() {
  const { t } = useTranslation();

  useSEO({
    title: '공공기관 솔루션 - KR STUDIO | 공공교육 콘텐츠 제작',
    description: 'KR스튜디오의 공공기관 특화 솔루션으로 신뢰할 수 있는 교육 콘텐츠를 제작하세요. 공공 부문의 다양한 교육 요구사항을 충족합니다.',
    keywords: '공공기관 솔루션, 공공교육, 정부 교육, 공무원 교육, 공공 콘텐츠',
    canonical: '/public-solution',
    schema: createServiceSchema(
      '공공기관 솔루션',
      '공공 부문의 다양한 교육 요구사항을 충족하는 신뢰할 수 있는 콘텐츠 제작'
    ),
  });

  const cases = [
    {
      organization: 'A공공기관',
      image: 'https://public.readdy.ai/ai/img_res/bf3543fa33d4767ae8b4128801b71301.jpg',
      challenge: '국민 대상 정책 설명회 온라인 전환',
      solution: '웨비나 플랫폼 구축 및 실시간 송출',
      results: ['월 10회 이상 웨비나 진행', '평균 5,000명 참여', '질의응답 실시간 처리']
    },
    {
      organization: 'B지방자치단체',
      image: 'https://readdy.ai/api/search-image?query=Local%20government%20online%20education%20platform%20with%20civil%20servants%20attending%20virtual%20training%2C%20modern%20e-learning%20system%2C%20professional%20government%20facility%2C%20clean%20organized%20environment&width=800&height=600&seq=pub2&orientation=landscape',
      challenge: '공무원 교육 콘텐츠 제작 및 관리',
      solution: '교육 콘텐츠 제작 및 LMS 구축',
      results: ['연간 200개 교육과정', '전 직원 온라인 교육', '교육 비용 60% 절감']
    },
    {
      organization: 'C공사',
      image: 'https://readdy.ai/api/search-image?query=Public%20corporation%20safety%20training%20video%20production%20with%20workers%20learning%20safety%20procedures%2C%20professional%20educational%20content%2C%20industrial%20setting%2C%20clean%20professional%20atmosphere&width=800&height=600&seq=pub3&orientation=landscape',
      challenge: '안전교육 콘텐츠 표준화',
      solution: '안전교육 영상 시리즈 제작',
      results: ['50개 안전교육 영상', '사고율 30% 감소', '교육 이수율 98%']
    }
  ];

  const services = [
    {
      icon: 'ri-government-line',
      title: '정책 홍보',
      description: '국민 소통을 위한 정책 설명 콘텐츠',
      features: ['정책 설명 영상', '홍보 영상', '캠페인 영상', '다국어 자막']
    },
    {
      icon: 'ri-presentation-line',
      title: '온라인 행사',
      description: '대규모 온라인 행사 및 웨비나',
      features: ['실시간 송출', '대용량 접속', '양방향 소통', '녹화 제공']
    },
    {
      icon: 'ri-book-open-line',
      title: '교육 콘텐츠',
      description: '공무원 및 국민 대상 교육 자료',
      features: ['직무 교육', '법정 교육', '안전 교육', '이러닝 제작']
    },
    {
      icon: 'ri-shield-check-line',
      title: '보안 관리',
      description: '공공기관 보안 기준 준수',
      features: ['보안 서버', '접근 통제', '암호화', '감사 로그']
    }
  ];

  const features = [
    { title: '예산 효율화', description: '투명한 예산 집행 및 비용 절감', icon: 'ri-money-dollar-circle-line' },
    { title: '접근성 강화', description: '장애인 접근성 기준 준수', icon: 'ri-wheelchair-line' },
    { title: '보안 준수', description: '공공기관 보안 규정 완벽 대응', icon: 'ri-lock-line' },
    { title: '표준화', description: '정부 표준 포맷 및 규격 준수', icon: 'ri-file-list-3-line' },
    { title: '아카이빙', description: '장기 보관 및 관리 시스템', icon: 'ri-archive-line' },
    { title: '투명성', description: '제작 과정 및 결과 투명 공개', icon: 'ri-eye-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopBar />
      <MainNav />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900 text-white py-32">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20government%20building%20with%20digital%20technology%20integration%2C%20public%20service%20innovation%2C%20professional%20government%20facility%2C%20clean%20organized%20official%20environment&width=1920&height=800&seq=pubhero&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-black mb-6">공공 솔루션 사례</h1>
            <p className="text-2xl mb-8 text-gray-300">
              공공기관을 위한<br />
              신뢰할 수 있는 콘텐츠 솔루션
            </p>
            <a href="mailto:contact@krstudio.com" className="inline-block bg-slate-500 text-white px-12 py-4 text-lg font-bold hover:bg-opacity-90 transition cursor-pointer whitespace-nowrap hidden">
              공공 솔루션 문의
            </a>
          </div>
        </div>
      </section>

      {/* Cases Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">도입 사례</h2>
            <p className="text-xl text-gray-600">공공기관의 성공 사례</p>
          </div>
          <div className="space-y-16">
            {cases.map((item, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}>
                <div className="lg:w-1/2">
                  <div className="relative aspect-[16/10] overflow-hidden shadow-xl">
                    <img 
                      src={item.image} 
                      alt={item.organization}
                      className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                </div>
                <div className="lg:w-1/2">
                  <h3 className="text-3xl font-black mb-4">{item.organization}</h3>
                  <div className="mb-6">
                    <h4 className="text-lg font-bold text-slate-600 mb-2">과제</h4>
                    <p className="text-gray-700">{item.challenge}</p>
                  </div>
                  <div className="mb-6">
                    <h4 className="text-lg font-bold text-slate-600 mb-2">솔루션</h4>
                    <p className="text-gray-700">{item.solution}</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-600 mb-3">성과</h4>
                    <ul className="space-y-2">
                      {item.results.map((result, idx) => (
                        <li key={idx} className="flex items-center">
                          <i className="ri-checkbox-circle-fill text-slate-500 mr-3"></i>
                          <span className="font-bold text-gray-700">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">제공 서비스</h2>
            <p className="text-xl text-gray-600">공공기관 맞춤형 서비스</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-8 hover:shadow-xl transition-all group cursor-pointer">
                <div className="w-16 h-16 bg-slate-500/10 flex items-center justify-center mb-6 group-hover:bg-slate-500 transition-all">
                  <i className={`${service.icon} text-3xl text-slate-500 group-hover:text-white transition-all`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-700">
                      <i className="ri-check-line text-slate-500 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">공공기관 특화 기능</h2>
            <p className="text-xl text-gray-600">공공 부문에 최적화된 솔루션</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-8 hover:shadow-lg transition-all cursor-pointer">
                <div className="w-14 h-14 bg-slate-500/10 flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-2xl text-slate-500`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">규정 준수</h2>
            <p className="text-xl text-gray-600">공공기관 요구사항 완벽 대응</p>
          </div>
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
            <div className="bg-white p-8 hover:shadow-lg transition-all cursor-pointer">
              <div className="flex items-center mb-4">
                <i className="ri-shield-check-line text-3xl text-slate-500 mr-4"></i>
                <h3 className="text-xl font-bold">보안 규정</h3>
              </div>
              <p className="text-gray-600">국가정보원 보안 규정 및 개인정보보호법 준수</p>
            </div>
            <div className="bg-white p-8 hover:shadow-lg transition-all cursor-pointer">
              <div className="flex items-center mb-4">
                <i className="ri-wheelchair-line text-3xl text-slate-500 mr-4"></i>
                <h3 className="text-xl font-bold">웹 접근성</h3>
              </div>
              <p className="text-gray-600">한국형 웹 콘텐츠 접근성 지침(KWCAG) 준수</p>
            </div>
            <div className="bg-white p-8 hover:shadow-lg transition-all cursor-pointer">
              <div className="flex items-center mb-4">
                <i className="ri-file-list-3-line text-3xl text-slate-500 mr-4"></i>
                <h3 className="text-xl font-bold">표준 규격</h3>
              </div>
              <p className="text-gray-600">행정안전부 전자정부 표준프레임워크 적용</p>
            </div>
            <div className="bg-white p-8 hover:shadow-lg transition-all cursor-pointer">
              <div className="flex items-center mb-4">
                <i className="ri-archive-line text-3xl text-slate-500 mr-4"></i>
                <h3 className="text-xl font-bold">기록물 관리</h3>
              </div>
              <p className="text-gray-600">공공기록물 관리에 관한 법률 준수</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black mb-4">공공 솔루션 실적</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-black text-slate-400 mb-3">30+</div>
              <div className="text-xl">공공기관</div>
            </div>
            <div>
              <div className="text-5xl font-black text-slate-400 mb-3">300+</div>
              <div className="text-xl">프로젝트</div>
            </div>
            <div>
              <div className="text-5xl font-black text-slate-400 mb-3">100%</div>
              <div className="text-xl">규정 준수</div>
            </div>
            <div>
              <div className="text-5xl font-black text-slate-400 mb-3">97%</div>
              <div className="text-xl">재계약률</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-black mb-6">공공 서비스 혁신을 시작하세요</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            신뢰할 수 있는 파트너와 함께 국민 소통을 강화하세요
          </p>
          <a href="mailto:contact@krstudio.com" className="inline-block bg-slate-500 text-white px-12 py-4 text-lg font-bold hover:bg-opacity-90 transition cursor-pointer whitespace-nowrap hidden">
            무료 컨설팅 신청
          </a>
        </div>
      </section>

      <Footer />
      <QuickMenu />
    </div>
  );
}